import React, { FunctionComponent, useEffect, useState } from 'react';
import { checkEmail } from 'utils/FormValidation';
import Styles from 'views/profile/Profile.module.scss';
import InputField from 'components/textField/InputField';
import { LOADER_STATUS } from 'store/constants';
import { getProfileAvatarUrl } from 'store/services/URL';
import { useHistory } from 'react-router';
import Toast from 'components/toast/Tost';
import { trackFormInteraction } from 'utils/clarity';
import SettingsActionButtons from 'components/settingsActionButtons';

import editProfileIcon from 'assets/images/profile-icons/edit-profile.png';
import personalInfoIcon from 'assets/images/profile-icons/personal-info.png';
import changePasswordIcon from 'assets/images/profile-icons/change-password.png';
import deleteBinIcon from 'assets/images/profile-icons/delete-bin.png';

interface ProfileProps {
  updateProfile: Function;
  deleteProfile: Function;
  loading: boolean;
  response: string;
}

const Profile: FunctionComponent<ProfileProps> = (props: ProfileProps) => {
  const { updateProfile, loading, deleteProfile } = props;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [name, setName] = useState<string>(user.name || '');
  const [email, setEmail] = useState<string>(user.email || '');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [oldPassword, setOldPassword] = useState<string>('');
  const [file, setFile] = useState<any>('');
  const [image, setImage] = useState<any>(getProfileAvatarUrl(user.profile));
  const [emailError, setEmailError] = useState<string>('');
  const [requiredField, setRequiredField] = useState<string[]>([]);
  const [isCheckLoading, setIsCheckLoading] = useState<number>(LOADER_STATUS.NA);
  const [isConfirm, setIsConfirm] = useState<boolean>(false);
  const [alert, setAlert] = useState(false);
  const history = useHistory();
  const [toast, setToast] = useState(false);
  const [toastValues, setToastValues] = useState({ message: '', type: '' });
  const isSubmittedRef = React.useRef(false);

  useEffect(() => {
    trackFormInteraction("edit_profile", "opened");
    return () => {
      if (!isSubmittedRef.current) {
        trackFormInteraction("edit_profile", "cancelled");
      }
    };
  }, []);

  const handleBlur = async (event: React.FormEvent<HTMLInputElement>) => {
    const val = event.currentTarget.value;
    const error = checkEmail(val);
    setEmailError(error);
    if (error.length === 0 && user.email !== val) {
      removeFromErrorList('email');
      setEmailError('');
      try {
        setIsCheckLoading(LOADER_STATUS.PENDING);
      } catch (err: any) {
        setIsCheckLoading(LOADER_STATUS.COMPLETE);
      }
    }
  };

  const handleUpdate = () => {
    const errors: string[] = [];
    if (name.length === 0) errors.push('name');
    if (email.length === 0) errors.push('email');
    if (errors.length > 0) {
      setRequiredField(errors);
      trackFormInteraction("edit_profile", "validation_error", errors.join(","));
      return;
    }
    if (newPassword !== confirmPassword) {
      setIsConfirm(true);
      trackFormInteraction("edit_profile", "validation_error", "password_mismatch");
      return;
    } else {
      setIsConfirm(false);
    }
    setRequiredField([]);
    const formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    if (oldPassword) formData.append('old_password', oldPassword);
    if (newPassword) formData.append('new_password', newPassword);
    if (file) formData.append('profile_pic', file);
    isSubmittedRef.current = true;
    trackFormInteraction("edit_profile", "submitted");
    updateProfile(formData);
    setToast(true);
    setToastValues({ message: 'Your changes have been saved.', type: 'Success' });
  };

  const removeFromErrorList = (val: string) => {
    if (!val) return;
    setRequiredField(requiredField.filter((x: string) => x.toLowerCase() !== val.toLowerCase()));
  };

  const isInError = (fieldName: string) => {
    if (requiredField.includes(fieldName)) return `${fieldName} is required`;
    return '';
  };

  const handleCancel = () => {
    trackFormInteraction("edit_profile", "cancelled");
    setName(user.name || '');
    setEmail(user.email || '');
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setImage(getProfileAvatarUrl(user.profile));
    setFile('');
  };

  const handleChangeImage = (file: any) => {
    const fileType = file.target.files[0].type;
    const type = fileType.split('/');
    if (type[0] === 'image') {
      setAlert(false);
      const path: any = URL.createObjectURL(file.target.files[0]);
      setImage(path);
      setFile(file.target.files[0]);
    } else {
      setImage(getProfileAvatarUrl(user.profile));
      setFile('');
      setAlert(true);
    }
  };

  const handleDelete = () => {
    deleteProfile({});
    localStorage.removeItem('user');
    localStorage.clear();
    history.push('/login');
  };

  return (
    <div className={Styles.settingsContent}>

      <div className={`${Styles.card} ${Styles.profilePicCard}`}>
        <div className={Styles.profilePicRow}>
          <div className={`position-relative ${Styles.profileImageInner}`}>
            <img src={image} className={Styles.profileImage} alt="profile" />
            <span className={Styles.editBadge}>
              <img src={editProfileIcon} alt="edit profile" className={Styles.editBadgeIcon} />
              <input
                onChange={handleChangeImage}
                accept={'image/*'}
                type="file"
                className={Styles.fileUpload}
              />
            </span>
          </div>
          <div className={Styles.profilePicText}>
            <h5 className={Styles.picTitle}>CHANGE PROFILE PICTURE</h5>
            <p className={Styles.picDesc}>
              Profile picture should be in the standard format: png, jpg &amp; no more than 3MB.
            </p>
            {alert && <p className={Styles.errorText}>Only Accept Image (.jpg, .jpeg, .png) etc</p>}
          </div>
        </div>
      </div>

      <div className={Styles.twoColRow}>
        <div className={`${Styles.card} ${Styles.stretchCard}`}>
          <div className={Styles.cardHeader}>
            <img src={personalInfoIcon} alt="" className={Styles.cardHeaderIconRound} />
            <span className={Styles.cardTitle}>PERSONAL INFO</span>
          </div>
          <div className={Styles.fieldGroup}>
            <InputField
              label="Full Name"
              handleChange={setName}
              type="text"
              cssClass={Styles.inputStyle}
              value={name}
              placeholder="Enter Full Name"
            />
            <span className={Styles.fieldError}>{isInError('name')}</span>
          </div>
          <div className={Styles.fieldGroup}>
            <InputField
              label="Email"
              blurHandler={handleBlur}
              handleChange={setEmail}
              type="email"
              cssClass={Styles.inputStyle}
              value={email}
              placeholder="Enter Email"
              showLoading={isCheckLoading === LOADER_STATUS.PENDING}
              errorMessage={emailError}
            />
            <span className={Styles.fieldError}>{isInError('email')}</span>
          </div>
        </div>

        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <img src={changePasswordIcon} alt="" className={Styles.cardHeaderIconRound} />
            <span className={Styles.cardTitle}>CHANGE PASSWORD</span>
          </div>
          <div className={Styles.fieldGroup}>
            <InputField
              label="Current Password"
              handleChange={setOldPassword}
              type="password"
              cssClass={Styles.inputStyle}
              value={oldPassword}
              placeholder="Enter Current Password"
            />
          </div>
          <div className={Styles.passwordRow}>
            <div className={Styles.fieldGroup}>
              <InputField
                label="New Password"
                handleChange={setNewPassword}
                type="password"
                cssClass={Styles.inputStyle}
                value={newPassword}
                placeholder="Enter New Password"
              />
            </div>
            <div className={Styles.fieldGroup}>
              <InputField
                label="Confirm Password"
                handleChange={setConfirmPassword}
                type="password"
                cssClass={Styles.inputStyle}
                value={confirmPassword}
                placeholder="Enter Confirm Password"
              />
            </div>
          </div>
          {isConfirm && (
            <p className={Styles.errorText}>Password and confirm password do not match!</p>
          )}
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.deleteCard}`}>
        <div className={Styles.deleteRow}>
          <div className={Styles.deleteLeft}>
            <span className={Styles.deleteIcon}>
              <img src={deleteBinIcon} alt="" className={Styles.deleteIconImg} />
            </span>
            <div>
              <h5 className={Styles.deleteTitle}>DELETE ACCOUNT</h5>
              <p className={Styles.deleteDesc}>By deleting your account, you will lose all your data</p>
            </div>
          </div>
          <button
            className={Styles.deleteBtn}
            onClick={handleDelete}
            onKeyUp={handleDelete}
            type="button"
          >
            Delete Account
            <img src={deleteBinIcon} alt="" className={Styles.deleteBtnIcon} />
          </button>
        </div>
      </div>

      <SettingsActionButtons
        onCancel={handleCancel}
        onSave={handleUpdate}
        saving={loading}
      />

      {toast && (
        <Toast
          message={toastValues.message}
          position="topRight"
          type={toastValues.type}
          handleCloseToast={setToast}
        />
      )}
    </div>
  );
};

export default Profile;
